/**
 * Join-path diagnostics: network, firewall, VPN, bandwidth, device, browser, A/V permissions.
 * Probe-only — does not open camera/mic streams.
 */

const STATUS = {
  PASS: 'pass',
  WARN: 'warn',
  FAIL: 'fail'
}

const STUN_URLS = ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302']
const ICE_TIMEOUT_MS = 6000
const NETWORK_TIMEOUT_MS = 6000

// Overlay/VPN ranges only. Private LAN (10/8, 192.168/16) is excluded on purpose.
const VPN_CIDRS = [
  ['10.8.0.0', 16],
  ['10.7.0.0', 16],
  ['10.9.0.0', 16],
  ['100.64.0.0', 10],
  ['25.0.0.0', 8],
  ['26.0.0.0', 8]
]

function result (id, status, detailKey, suggestionKey = null, params = {}) {
  return { id, status, detailKey, suggestionKey, params }
}

function withTimeout (promise, ms) {
  let timer
  return Promise.race([
    promise,
    new Promise((resolve) => {
      timer = setTimeout(() => resolve(null), ms)
    })
  ]).finally(() => clearTimeout(timer))
}

function ipv4ToInt (ip) {
  const parts = String(ip).split('.')
  if (parts.length !== 4) return null
  let value = 0
  for (let i = 0; i < 4; i++) {
    const octet = Number(parts[i])
    if (!Number.isInteger(octet) || octet < 0 || octet > 255) return null
    value = (value << 8) + octet
  }
  return value >>> 0
}

function isVpnLikeAddress (address) {
  if (!address || address.includes(':')) return false
  const ip = ipv4ToInt(address)
  if (ip === null) return false
  return VPN_CIDRS.some(([cidr, bits]) => {
    const net = ipv4ToInt(cidr)
    if (net === null) return false
    const mask = bits === 0 ? 0 : (0xffffffff << (32 - bits)) >>> 0
    return (ip & mask) === (net & mask)
  })
}

function candidateAddress (candidate) {
  if (!candidate) return null
  if (candidate.address) return candidate.address
  const parts = String(candidate.candidate || '').split(' ')
  return parts.length >= 5 ? parts[4] : null
}

function candidateType (candidate) {
  if (!candidate) return null
  if (candidate.type) return candidate.type
  const match = / typ ([a-z]+)/.exec(candidate.candidate || '')
  return match ? match[1] : null
}

async function gatherIce () {
  if (typeof RTCPeerConnection === 'undefined') {
    return { supported: false, error: false, count: 0, hasHost: false, hasSrflx: false, hasRelay: false, addresses: [] }
  }

  const pc = new RTCPeerConnection({ iceServers: [{ urls: STUN_URLS }] })
  const candidates = []

  try {
    pc.createDataChannel('wsd-ts')
    pc.onicecandidate = (event) => {
      if (event.candidate) candidates.push(event.candidate)
    }
    await pc.setLocalDescription(await pc.createOffer())
    await withTimeout(new Promise((resolve) => {
      if (pc.iceGatheringState === 'complete') {
        resolve()
        return
      }
      pc.onicegatheringstatechange = () => {
        if (pc.iceGatheringState === 'complete') resolve()
      }
    }), ICE_TIMEOUT_MS)
  } catch (e) {
    return { supported: true, error: true, count: 0, hasHost: false, hasSrflx: false, hasRelay: false, addresses: [] }
  } finally {
    try { pc.close() } catch (e) { /* ignore */ }
  }

  const types = candidates.map(candidateType).filter(Boolean)
  return {
    supported: true,
    error: false,
    count: candidates.length,
    hasHost: types.includes('host'),
    hasSrflx: types.includes('srflx'),
    hasRelay: types.includes('relay'),
    addresses: candidates.map(candidateAddress).filter(Boolean)
  }
}

async function checkInternet () {
  if (navigator.onLine === false) {
    return result('internet', STATUS.FAIL, 'internet_offline', 'internet_suggestion')
  }

  const targets = [process.env.VUE_APP_API_HOST, window.location.origin].filter(Boolean)
  for (const target of targets) {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), NETWORK_TIMEOUT_MS)
    try {
      await fetch(`${String(target).replace(/\/?$/, '/')}?wsd_ts=${Date.now()}`, {
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal
      })
      clearTimeout(timer)
      return result('internet', STATUS.PASS, 'internet_ok')
    } catch (e) {
      clearTimeout(timer)
    }
  }

  return result('internet', STATUS.FAIL, 'internet_unreachable', 'internet_suggestion')
}

function checkFirewall (ice) {
  if (!ice.supported) {
    return result('firewall', STATUS.FAIL, 'firewall_no_webrtc', 'browser_suggestion')
  }
  if (ice.error) {
    return result('firewall', STATUS.FAIL, 'firewall_ice_error', 'firewall_suggestion')
  }
  if (ice.hasSrflx || ice.hasRelay) {
    return result('firewall', STATUS.PASS, 'firewall_ok')
  }
  if (ice.hasHost && ice.count > 0) {
    return result('firewall', STATUS.FAIL, 'firewall_stun_blocked', 'firewall_suggestion')
  }
  return result('firewall', STATUS.FAIL, 'firewall_no_candidates', 'firewall_suggestion')
}

function checkVpn (ice) {
  if (ice.addresses.some(isVpnLikeAddress)) {
    return result('vpn', STATUS.WARN, 'vpn_detected', 'vpn_suggestion')
  }
  return result('vpn', STATUS.PASS, 'vpn_ok')
}

function checkBandwidth () {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
  if (!connection) {
    return result('bandwidth', STATUS.PASS, 'bandwidth_unknown')
  }

  const type = connection.effectiveType
  const downlink = typeof connection.downlink === 'number' ? connection.downlink : null
  const rtt = typeof connection.rtt === 'number' ? connection.rtt : null
  const params = {
    type: type || 'unknown',
    downlink: downlink != null ? String(downlink) : '—',
    rtt: rtt != null ? String(rtt) : '—'
  }

  if (connection.saveData) {
    return result('bandwidth', STATUS.WARN, 'bandwidth_save_data', 'bandwidth_suggestion', params)
  }
  if (type === 'slow-2g' || type === '2g') {
    return result('bandwidth', STATUS.FAIL, 'bandwidth_too_slow', 'bandwidth_suggestion', params)
  }
  if (type === '3g' || (downlink != null && downlink > 0 && downlink < 1.5)) {
    return result('bandwidth', STATUS.WARN, 'bandwidth_low', 'bandwidth_suggestion', params)
  }
  if (rtt != null && rtt > 400) {
    return result('bandwidth', STATUS.WARN, 'bandwidth_high_latency', 'bandwidth_suggestion', params)
  }
  return result('bandwidth', STATUS.PASS, 'bandwidth_ok', null, params)
}

function checkCpu () {
  const cores = navigator.hardwareConcurrency || null
  const memory = navigator.deviceMemory || null
  const params = {
    cores: cores != null ? String(cores) : '—',
    memory: memory != null ? String(memory) : '—'
  }

  if ((memory != null && memory < 1) || (cores != null && cores < 2)) {
    return result('cpu', STATUS.FAIL, 'cpu_too_slow', 'cpu_suggestion', params)
  }
  if ((cores != null && cores <= 2) || (memory != null && memory <= 2)) {
    return result('cpu', STATUS.WARN, 'cpu_limited', 'cpu_suggestion', params)
  }
  return result('cpu', STATUS.PASS, 'cpu_ok', null, params)
}

async function checkCache () {
  try {
    window.localStorage.setItem('__wsd_join_ts', '1')
    window.localStorage.removeItem('__wsd_join_ts')
  } catch (e) {
    return result('cache', STATUS.WARN, 'cache_storage_blocked', 'cache_suggestion')
  }

  try {
    if (navigator.serviceWorker) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      if (registrations.some((registration) => registration.waiting)) {
        return result('cache', STATUS.WARN, 'cache_update_waiting', 'cache_suggestion')
      }
    }
  } catch (e) {
    // ignore SW probe errors
  }

  return result('cache', STATUS.PASS, 'cache_ok')
}

function checkBrowser () {
  const detect = window.DetectRTC || {}
  const name = (detect.browser && detect.browser.name) || 'unknown'
  const version = detect.browser && detect.browser.version ? String(detect.browser.version) : ''
  const params = { browser: version ? `${name} ${version}` : name }

  if (window.isWeSeeDoSupported === false) {
    return result('browser', STATUS.FAIL, 'browser_unsupported', 'browser_suggestion', params)
  }
  if (detect.isWebRTCSupported === false) {
    return result('browser', STATUS.FAIL, 'browser_no_webrtc', 'browser_suggestion', params)
  }
  return result('browser', STATUS.PASS, 'browser_ok', null, params)
}

function mediaResult (id, devices, permissionState) {
  if (permissionState === 'denied') {
    return result(id, STATUS.FAIL, `${id}_denied`, `${id}_suggestion`)
  }
  if (!devices.length) {
    return result(id, STATUS.FAIL, `${id}_not_found`, `${id}_suggestion`)
  }
  if (permissionState === 'prompt' || devices.every((device) => !device.label)) {
    return result(id, STATUS.WARN, `${id}_not_granted`, `${id}_suggestion`)
  }
  return result(id, STATUS.PASS, `${id}_ok`, null, { count: String(devices.length) })
}

async function checkMedia () {
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.enumerateDevices !== 'function') {
    return [
      result('camera', STATUS.FAIL, 'camera_unsupported', 'camera_suggestion'),
      result('microphone', STATUS.FAIL, 'microphone_unsupported', 'microphone_suggestion')
    ]
  }

  let cameraPermission = null
  let microphonePermission = null
  if (navigator.permissions && navigator.permissions.query) {
    try {
      cameraPermission = (await navigator.permissions.query({ name: 'camera' })).state
    } catch (e) { /* unsupported permission name */ }
    try {
      microphonePermission = (await navigator.permissions.query({ name: 'microphone' })).state
    } catch (e) { /* unsupported permission name */ }
  }

  let devices
  try {
    devices = await navigator.mediaDevices.enumerateDevices()
  } catch (e) {
    return [
      result('camera', STATUS.FAIL, 'camera_enumerate_failed', 'camera_suggestion'),
      result('microphone', STATUS.FAIL, 'microphone_enumerate_failed', 'microphone_suggestion')
    ]
  }

  return [
    mediaResult('camera', devices.filter((device) => device.kind === 'videoinput'), cameraPermission),
    mediaResult('microphone', devices.filter((device) => device.kind === 'audioinput'), microphonePermission)
  ]
}

function checkSecureContext () {
  const host = window.location.hostname
  if (window.isSecureContext || host === 'localhost' || host === '127.0.0.1') {
    return result('secure', STATUS.PASS, 'secure_ok')
  }
  return result('secure', STATUS.FAIL, 'secure_insecure', 'secure_suggestion')
}

function summarize (checks) {
  return {
    checks,
    failCount: checks.filter((item) => item.status === STATUS.FAIL).length,
    warnCount: checks.filter((item) => item.status === STATUS.WARN).length
  }
}

export async function runJoinTroubleshoot ({ onProgress } = {}) {
  const checks = []
  const emit = typeof onProgress === 'function' ? onProgress : null
  const push = (item) => {
    checks.push(item)
    if (emit) emit(checks.slice())
  }
  const pushMany = (items) => {
    checks.push(...items)
    if (emit) emit(checks.slice())
  }

  push(await checkInternet())

  const ice = await gatherIce()
  push(checkFirewall(ice))
  push(checkVpn(ice))
  push(checkBandwidth())
  push(checkBrowser())
  push(checkSecureContext())

  const [cpu, cache, media] = await Promise.all([
    checkCpu(),
    checkCache(),
    checkMedia()
  ])
  push(cpu)
  push(cache)
  pushMany(media)

  return summarize(checks)
}

export async function clearAppCachesAndReload () {
  try {
    if (navigator.serviceWorker) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((registration) => registration.unregister()))
    }
  } catch (e) { /* continue */ }

  try {
    if (window.caches) {
      const keys = await window.caches.keys()
      await Promise.all(keys.map((key) => window.caches.delete(key)))
    }
  } catch (e) { /* continue */ }

  window.location.reload()
}
